import { FieldFunctionOptions } from "@apollo/client";
import { uniqBy } from "lodash";

interface ResponseObject {
  __typename?: string;
}

interface PaginatedResponse<T> {
  totalCount: number;
  objects: T[];
}

export const mergePaginatedData = <T extends ResponseObject>(
  existing: Pick<PaginatedResponse<T>, "objects"> | undefined,
  incoming: PaginatedResponse<T>,
  options: FieldFunctionOptions,
): PaginatedResponse<T> => {
  const existingObjects = existing?.objects || [];
  const incomingObjects = incoming.objects || [];
  const paginationArgs = options?.args?.pagination || {
    offset: 0,
    limit: incomingObjects.length,
  };
  const { offset, limit } = paginationArgs;
  const mergedObjects = [...existingObjects];
  mergedObjects.splice(offset, limit, ...incomingObjects);
  return {
    totalCount: incoming.totalCount,
    objects: uniqBy(mergedObjects, "__ref"),
  };
};

interface ActionPermissionsResponse<T> {
  totalCount?: number | null;
  enabledCount?: number | null;
  objects?: T[];
}

export const mergeActionPermissionsData = <T>(
  existing: ActionPermissionsResponse<T> | undefined,
  incoming: ActionPermissionsResponse<T>,
  options: FieldFunctionOptions,
): ActionPermissionsResponse<T> => {
  const merged = { ...existing, ...incoming };
  // EmployeeActionPermission rows have no id, so they are stored inline and
  // cannot be deduped by ref; splice strictly by the requested page window.
  if (incoming.objects === undefined) return merged;
  const incomingObjects = incoming.objects;
  const pagination = options?.args?.pagination;
  if (!pagination) return { ...merged, objects: incomingObjects };
  const { offset = 0, limit = incomingObjects.length } = pagination;
  const objects = [...(existing?.objects ?? [])];
  objects.splice(offset, limit, ...incomingObjects);
  return { ...merged, objects };
};

interface CreditLedgerPage<T> {
  totalCount?: number;
  entries?: T[];
}

/**
 * Ledger rows carry no id, so they cache inline with no `__ref` to dedupe on —
 * {@link mergePaginatedData}'s `uniqBy` would collapse every page into one row.
 */
export const mergeCreditLedgerPages = <T>(
  existing: CreditLedgerPage<T> | undefined,
  incoming: CreditLedgerPage<T>,
  options: FieldFunctionOptions,
): CreditLedgerPage<T> => {
  const merged = { ...existing, ...incoming };
  if (incoming.entries === undefined) return merged;
  const incomingEntries = incoming.entries;
  const pagination = options?.args?.pagination;
  if (!pagination) return { ...merged, entries: incomingEntries };
  const { offset = 0, limit = incomingEntries.length } = pagination;
  // Offset 0 is a refresh, not a page. Splicing a fresh first page onto a
  // cached tail drops an entry for every row that has appeared since (the tail
  // has already shifted down), and a period that now has no spends at all would
  // keep its stale tail under a totalCount of 0.
  if (offset === 0) return { ...merged, entries: incomingEntries };
  const entries = [...(existing?.entries ?? [])];
  entries.splice(offset, limit, ...incomingEntries);
  return { ...merged, entries };
};
